import React, { useEffect, useState, useRef } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useForm, Controller } from "react-hook-form";
import dayjs from "dayjs";
import {
  Grid,
  Paper,
  Typography,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  CircularProgress,
} from "@mui/material";

import WSTextField, { TextType } from "../../Components/TextBox/WSTextBox";
import {
  callAPI,
  Method,
  handleApiError,
  updateMainHeader,
  LoacalVariables,
} from "../../commonFunctions/CommonFunctions";
import { useSnackbar } from "../../Components/Snackbar/SnackBarContext";
import { breadCrumbsStore } from "../../Components/BreadCrumbs/BreadCrumbsListner";
import {
  HeaderList,
  IHeaderListActions,
} from "../../Components/Header/HeaderList";

interface IVendorOption {
  id: string;
  name: string;
}

interface IBillOption {
  id: string;
  billNo: string;
  amount: number;
}

interface IVendorPaymentForm {
  vendorID: string;
  billIDs: string[];
  paymentDate: string;
  paymentMode: string;
  amountPaid: number | string;
  referenceNo: string;
}

export const CreateVendorPayment: React.FC<any> = React.memo(() => {
  const { showMessage } = useSnackbar();
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  // console.log("🧭 useParams id:", id);

  const isEditMode = !!id;

  const [vendorOptions, setVendorOptions] = useState<IVendorOption[]>([]);
  const [billOptions, setBillOptions] = useState<IBillOption[]>([]);
  const [loading, setLoading] = useState(false);
  const [initialLoading, setInitialLoading] = useState(true);

  const formRef = useRef<HTMLFormElement>(null);

  const { control, handleSubmit, reset, watch, setValue } =
    useForm<IVendorPaymentForm>({
      defaultValues: {
        vendorID: "",
        billIDs: [],
        paymentDate: dayjs().format("YYYY-MM-DD"),
        paymentMode: "",
        amountPaid: "",
        referenceNo: "",
      },
    });

  const selectedVendor = watch("vendorID");

  // ✅ Fetch vendor list and edit payment data
  useEffect(() => {
    const fetchData = async () => {
      try {
        const vendors: any = await callAPI("Vendor", Method.GET);
        const vendorList = (Array.isArray(vendors)
          ? vendors
          : vendors?.data || []
        ).map((v: any) => ({
          id: v.vendorID || v.id || v._id,
          name: v.vendorName || v.name,
        }));
        setVendorOptions(vendorList);

        if (isEditMode && id) {
  const paymentResponse: any = await callAPI(
    `VendorPayment/paymentId/${id}`,
    Method.GET
  );
  console.log("🔍 Payment data from API:", paymentResponse);

  const payment = paymentResponse?.data || paymentResponse;
  if (!payment) {
    showMessage("No payment data found for this ID", "error");
    return;
  }

  const vendorId = payment.vendorID || payment.vendor?.vendorID || "";
  if (!vendorId) {
    console.error("⚠️ Missing vendorID in payment data:", payment);
    showMessage("Vendor ID missing in payment data", "error");
    return;
  }

  // ✅ Fetch vendor's bills safely
  const billsRes: any = await callAPI(
    `VendorBill/ByVendorId/${vendorId}`,
    Method.GET
  );
  const billsArray = Array.isArray(billsRes)
    ? billsRes
    : Array.isArray(billsRes?.data)
    ? billsRes.data
    : [billsRes];

  const bills = billsArray
    .filter((b: any) => b.billID || b.id)
    .map((b: any) => ({
      id: b.billID || b.id,
      billNo: b.billNo || "Unknown Bill",
      amount: b.totalAmount || 0,
    }));
  setBillOptions(bills);

  // ✅ Extract bill IDs properly
  const billIDs =
    Array.isArray(payment.billIDs) && payment.billIDs.length > 0
      ? payment.billIDs
      : Array.isArray(payment.bill)
      ? payment.bill.map((b: any) => b.id || b.billID)
      : [];

  // ✅ Reset form values safely
  reset({
    vendorID: vendorId,
    billIDs,
    paymentDate: payment.paymentDate
      ? dayjs(payment.paymentDate).format("YYYY-MM-DD")
      : dayjs().format("YYYY-MM-DD"),
    paymentMode: payment.paymentMode || "",
    amountPaid: payment.amountPaid || "",
    referenceNo: payment.referenceNo || "",
  });

  console.log("✅ Loaded payment for edit:", {
    vendorID: vendorId,
    billIDs,
    paymentMode: payment.paymentMode,
  });


        }
      } catch (err) {
        console.error("Error fetching data:", err);
        showMessage("Failed to fetch vendors or payment data", "error");
      } finally {
        setInitialLoading(false);
      }
    };

    fetchData();
  }, [id, isEditMode, reset, showMessage]);

  // ✅ Fetch bills for selected vendor
  useEffect(() => {
    const fetchBills = async () => {
      if (!selectedVendor) {
        setBillOptions([]);
        setValue("billIDs", []);
        return;
      }

      try {
        const res: any = await callAPI(
          `VendorBill/ByVendorId/${selectedVendor}`,
          Method.GET
        );

        const billsArray = Array.isArray(res)
          ? res
          : Array.isArray(res?.data)
          ? res.data
          : [res];

        const bills = billsArray.map((b: any) => ({
          id: b.billID || b.id,
          billNo: b.billNo || "Unknown Bill",
          amount: b.totalAmount || 0,
        }));

        setBillOptions(bills);
        setValue("billIDs", []);
      } catch (err) {
        console.error("Error fetching bills:", err);
        setBillOptions([]);
        showMessage("Failed to fetch vendor bills", "error");
      }
    };

    if (!isEditMode) fetchBills();
  }, [selectedVendor, setValue, showMessage, isEditMode]);

  // Header setup
  useEffect(() => {
    const title = isEditMode ? "Edit Vendor Payment" : "Create Vendor Payment";
    updateMainHeader(title);
    breadCrumbsStore.updateBreadCrumbs([
      { "Vendor Payments": "/vendorpayment/list" },
      { [title]: "" },
    ]);
  }, [isEditMode]);



 useEffect(() => {
    const handleEnter = (e: KeyboardEvent) => {
      if (e.key === "Enter") {
        e.preventDefault();
        formRef.current?.requestSubmit();
      }
    };
    window.addEventListener("keydown", handleEnter);
    return () => window.removeEventListener("keydown", handleEnter);
  }, []);



  const handleCancel = () => navigate("/vendorpayment/list");

  // ✅ Submit form
 const onSubmit = async (data: IVendorPaymentForm) => {
  console.log("data", data);
  const formattedPaymentDate = dayjs(data.paymentDate).toISOString();
  const userId = localStorage.getItem(LoacalVariables.UserId);

  if (!userId) {
    showMessage("User session expired. Please log in again.", "error");
    return;
  }

  const payload = {
    // paymentId: id,
    // vendorID: data.vendorID,
    billIDs: data.billIDs || [],
    paymentDate: formattedPaymentDate,
    paymentMode: data.paymentMode || "",
    amountPaid: Number(data.amountPaid) || 0,
    referenceNo: data.referenceNo || "",
  };

  try {
    setLoading(true);
    let response: any;

    if (isEditMode && id) {
      console.log("Updating payment with ID:", id, "userId:", userId);

      response = await callAPI(
        "VendorPayment",
        Method.PUT,
        payload,
        `${id}?updatedByUserId=${userId}`
      );
    } else {
      response = await callAPI(
        `VendorPayment/${data.vendorID}?createdByUserId=${userId}`,
        Method.POST,
        payload
      );
    }

    console.log("API response:", response);

    if (response?.error || response?.errors) {
      showMessage(handleApiError(response), "error");
    } else {
      showMessage(
        isEditMode ? "Vendor Payment updated successfully" : "Vendor Payment added successfully",
        "success"
      );
      navigate("/vendorpayment/list");
    }
  } catch (err) {
    console.error("Error submitting Vendor Payment:", err);
    showMessage("Error submitting Vendor Payment", "error");
  } finally {
    setLoading(false);
  }
};


  const actions: IHeaderListActions[] = [
    {
      type: "button",
      text: "Cancel",
      onClick: handleCancel,
      size: "small",
      variant: "outlined",
      color: "error",
    },
    {
      type: "button",
      text: isEditMode ? "Update Payment" : "Save Payment",
      loading,
      buttonType: "submit",
      size: "small",
      variant: "outlined",
      color: "primary",
    },
  ];

  if (initialLoading) {
    return (
      <Paper sx={{ p: 3, display: "flex", justifyContent: "center", mt: 2 }}>
        <CircularProgress />
      </Paper>
    );
  }

  return (
    <form ref={formRef} onSubmit={handleSubmit(onSubmit)}>
      <HeaderList
        title={isEditMode ? "Edit Vendor Payment" : "Create Vendor Payment"}
        isBackButton
        url="/vendorpayment/list"
        type="card"
        actions={actions}
        isDivider
        headerSx={{ height: 50 }}
      />
      <Paper sx={{ p: 3, mt: 1 }}>
        <Typography sx={{ fontSize: ".9rem" }} color="gray">
          Vendor Payment Details
        </Typography>
        <br />
        <Grid container spacing={2}>
          {/* Vendor */}
          <Grid item xs={12} sm={4}>
            <FormControl fullWidth size="small">
              <InputLabel id="vendor-label">Vendor</InputLabel>
              <Controller
                name="vendorID"
                control={control}
                render={({ field }) => (
                  <Select {...field} labelId="vendor-label" label="Vendor">
                    <MenuItem value="">Select Vendor</MenuItem>
                    {vendorOptions.map((v) => (
                      <MenuItem key={v.id} value={v.id}>
                        {v.name}
                      </MenuItem>
                    ))}
                  </Select>
                )}
              />
            </FormControl>
          </Grid>

          {/* Bills */}
          <Grid item xs={12} sm={4}>
            <FormControl fullWidth size="small">
              <InputLabel id="bill-label">Bills</InputLabel>
              <Controller
                name="billIDs"
                control={control}
                render={({ field }) => {
                  const handleChange = (event: any) =>
                    field.onChange(
                      Array.isArray(event.target.value)
                        ? event.target.value
                        : [event.target.value]
                    );
                  return (
                    <Select
                      multiple
                      {...field}
                      value={field.value || []}
                      onChange={handleChange}
                      labelId="bill-label"
                      label="Bills"
                      renderValue={(selected: string[]) =>
                        selected
                          .map(
                            (id) =>
                              billOptions.find((b) => b.id === id)?.billNo || id
                          )
                          .join(", ")
                      }
                    >
                      {billOptions.length === 0 ? (
                        <MenuItem disabled>No bills found</MenuItem>
                      ) : (
                        billOptions.map((b) => (
                          <MenuItem key={b.id} value={b.id}>
                            {b.billNo}
                          </MenuItem>
                        ))
                      )}
                    </Select>
                  );
                }}
              />
            </FormControl>
          </Grid>

          {/* Payment Date */}
          <Grid item xs={12} sm={4}>
            <Controller
              name="paymentDate"
              control={control}
              rules={{ required: " date is required" }}
              render={({ field }) => (
                <WSTextField
                  {...field}
                  control={control}
                  id="paymentDate"
                  label="Payment Date"
                  type={TextType.Date}
                />
              )}
            />
          </Grid>

          {/* Payment Mode */}
          <Grid item xs={12} sm={4}>
            <Controller
              name="paymentMode"
              control={control}
              rules={{ required: "payment mode  is required" }}
              render={({ field }) => (
                <WSTextField
                  {...field}
                  control={control}
                  id="paymentMode"
                  label="Payment Mode"
                />
              )}
            />
          </Grid>

          {/* Amount Paid */}
          <Grid item xs={12} sm={4}>
            <Controller
              name="amountPaid"
              control={control}
              render={({ field }) => (
                <WSTextField
                  {...field}
                  control={control}
                  id="amountPaid"
                  label="Amount Paid"
                  type={TextType.Number}
                />
              )}
            />
          </Grid>

          {/* Reference No */}
          <Grid item xs={12} sm={4}>
            <Controller
              name="referenceNo"
              control={control}
              rules={{ required: "refernce no is required" }}
              render={({ field }) => (
                <WSTextField
                  {...field}
                  control={control}
                  id="referenceNo"
                  label="Reference No"
                />
              )}
            />
          </Grid>
        </Grid>
      </Paper>
    </form>
  );
});